import React, { useState, useMemo, useRef } from 'react'
import Papa from 'papaparse'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { useFeature, useLimitCheck } from '@/lib/billing'
import { SHOW_BILLING_TAB } from '@/lib/featureFlags'
import { formatRelativeDate, getStatusColor } from '@/lib/utils'
import { Search, Plus, Users, Building2, Mail, Phone, X, Check, Trash2, ArrowUpDown, Lock, Sparkles, Download, Upload, GitMerge } from 'lucide-react'
import { toast } from 'sonner'
import Avatar from '@/components/Avatar'
import CompanyPicker from '@/components/CompanyPicker'
import MergeModal from '@/components/MergeModal'

const STATUSES = ['active', 'inactive', 'lead']
// Lifecycle stage (the sales-journey dimension; distinct from status). Token-only
// colors — text color distinguishes the 4 stages, neutral pill background.
const LIFECYCLES = ['lead', 'prospect', 'customer', 'churned']
const LIFECYCLE_TEXT: Record<string, string> = {
  lead: 'var(--ink-tertiary)', prospect: 'var(--info)', customer: 'var(--positive)', churned: 'var(--danger)',
}

// ─── CSV import (import/export CP2) ───────────────────────────────────────────
// Consumes CP1's exact 15-column export contract. Headers are case-insensitive +
// order-independent (Papa transformHeader); unknown columns ignored.
interface ContactImportRow {
  first_name: string
  last_name: string | null; email: string | null; phone: string | null
  company_name: string | null; status: string | null; tags: string[]
  lifecycle_stage: string | null; notes: string | null
  address_line1: string | null; address_line2: string | null
  address_city: string | null; address_state: string | null; address_zip: string | null; address_country: string | null
}
type ImportPreviewRow =
  | { kind: 'ok'; row: ContactImportRow; raw: Record<string, string>; index: number }
  | { kind: 'reject'; reason: string; raw: Record<string, string>; index: number }

const _t = (v?: string) => (v ?? '').trim()

function validateContactRow(raw: Record<string, string>, index: number): ImportPreviewRow {
  const first_name = _t(raw.first_name)
  if (!first_name) return { kind: 'reject', reason: 'missing first_name', raw, index }
  const email = _t(raw.email)
  if (email && !email.includes('@')) return { kind: 'reject', reason: 'invalid email', raw, index }
  // tags: reverse of CP1's '; ' join
  const tagsCell = _t(raw.tags)
  const tags = tagsCell ? tagsCell.split(';').map(s => s.trim()).filter(Boolean) : []
  return {
    kind: 'ok', raw, index,
    row: {
      first_name,
      last_name: _t(raw.last_name) || null,
      email: email || null,
      phone: _t(raw.phone) || null,
      company_name: _t(raw.company_name) || null,
      status: _t(raw.status) || null,
      tags,
      lifecycle_stage: _t(raw.lifecycle_stage) || null,
      notes: _t(raw.notes) || null,
      address_line1: _t(raw.address_line1) || null,
      address_line2: _t(raw.address_line2) || null,
      address_city: _t(raw.city) || null,
      address_state: _t(raw.state) || null,
      address_zip: _t(raw.zip) || null,
      address_country: _t(raw.country) || null,
    },
  }
}

function ContactsImportModal({ workspaceId, rows, onClose, onImported }: {
  workspaceId: string
  rows: ImportPreviewRow[]
  onClose: () => void
  onImported: () => void
}) {
  const queryClient = useQueryClient()
  const [updateExisting, setUpdateExisting] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [result, setResult] = useState<{ inserted: number; updated: number; skipped: number; rejected: number; failed: number; error?: string } | null>(null)

  // Dedup key (existing email→id) + company-by-name (name→id) maps.
  const { data: emailMap = new Map<string, string>() } = useQuery({
    queryKey: ['contacts-import-emailmap', workspaceId],
    queryFn: async () => {
      const { data } = await supabase.from('contacts').select('id,email').eq('workspace_id', workspaceId)
      const m = new Map<string, string>()
      for (const c of (data || []) as any[]) if (c.email) m.set(String(c.email).toLowerCase(), c.id)
      return m
    },
  })
  const { data: companyMap = new Map<string, string>() } = useQuery({
    queryKey: ['contacts-import-companymap', workspaceId],
    queryFn: async () => {
      const { data } = await supabase.from('companies').select('id,name').eq('workspace_id', workspaceId)
      const m = new Map<string, string>()
      for (const c of (data || []) as any[]) if (c.name) m.set(String(c.name).toLowerCase(), c.id)
      return m
    },
  })

  const okRows = rows.filter((r): r is Extract<ImportPreviewRow, { kind: 'ok' }> => r.kind === 'ok')
  const rejectedCount = rows.length - okRows.length
  const existingIdFor = (row: ContactImportRow) => (row.email ? emailMap.get(row.email.toLowerCase()) || null : null)
  const resolveCompany = (name: string | null) => (name ? companyMap.get(name.toLowerCase()) ?? null : null)
  const dupCount = okRows.filter(r => existingIdFor(r.row)).length
  const newCount = okRows.length - dupCount

  const handleCommit = async () => {
    setCommitting(true)
    let inserted = 0, updated = 0, skipped = 0, failed = 0
    const failures: string[] = []
    const newPayloads: any[] = []
    const updates: { id: string; payload: Record<string, any> }[] = []

    for (const r of okRows) {
      const existingId = existingIdFor(r.row)
      const companyId = resolveCompany(r.row.company_name)
      if (existingId) {
        if (!updateExisting) { skipped++; continue }
        // UPDATE: only columns the CSV actually provides — never blank an existing field.
        const u: Record<string, any> = {}
        const row = r.row
        if (row.first_name) u.first_name = row.first_name
        if (row.last_name) u.last_name = row.last_name
        if (row.email) u.email = row.email
        if (row.phone) u.phone = row.phone
        if (row.company_name) { u.company_name = row.company_name; u.company_id = companyId }
        if (row.status) u.status = row.status
        if (row.tags.length) u.tags = row.tags
        if (row.lifecycle_stage) u.lifecycle_stage = row.lifecycle_stage
        if (row.notes) u.notes = row.notes
        if (row.address_line1) u.address_line1 = row.address_line1
        if (row.address_line2) u.address_line2 = row.address_line2
        if (row.address_city) u.address_city = row.address_city
        if (row.address_state) u.address_state = row.address_state
        if (row.address_zip) u.address_zip = row.address_zip
        if (row.address_country) u.address_country = row.address_country
        updates.push({ id: existingId, payload: u })
      } else {
        const p: Record<string, any> = {
          workspace_id: workspaceId,
          first_name: r.row.first_name,
          last_name: r.row.last_name, email: r.row.email, phone: r.row.phone,
          company_id: companyId, company_name: r.row.company_name,
          status: r.row.status, tags: r.row.tags.length ? r.row.tags : null, notes: r.row.notes,
          address_line1: r.row.address_line1, address_line2: r.row.address_line2,
          address_city: r.row.address_city, address_state: r.row.address_state,
          address_zip: r.row.address_zip, address_country: r.row.address_country,
        }
        if (r.row.lifecycle_stage) p.lifecycle_stage = r.row.lifecycle_stage   // else DB default 'lead'
        newPayloads.push(p)
      }
    }

    // NEW rows → chunked bulk insert (limit blast radius; good chunks stay committed).
    const CHUNK = 500
    for (let i = 0; i < newPayloads.length; i += CHUNK) {
      const slice = newPayloads.slice(i, i + CHUNK)
      const { error } = await supabase.from('contacts').insert(slice)
      if (error) { failed += slice.length; failures.push(error.message) } else { inserted += slice.length }
    }
    // DUPLICATES (update mode) → per-row update of provided fields only.
    for (const up of updates) {
      const { error } = await supabase.from('contacts').update(up.payload).eq('id', up.id)
      if (error) { failed++; failures.push(error.message) } else { updated++ }
    }

    setCommitting(false)
    setResult({ inserted, updated, skipped, rejected: rejectedCount, failed, error: failures[0] })
    queryClient.invalidateQueries({ queryKey: ['contacts'] })
    if (failed === 0) toast.success(`Imported ${inserted} new${updated ? `, updated ${updated}` : ''}${skipped ? `, skipped ${skipped}` : ''}`)
    else toast.error(`Import finished with ${failed} failure(s)`)
    onImported()
  }

  const COLS = '36px 1.3fr 1.6fr 1fr 110px'
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 24, width: '100%', maxWidth: 820, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{result ? 'Import complete' : 'Preview import'}</h2>
            {!result && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>{newCount} new · {dupCount} duplicate · {rejectedCount} rejected.</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>

        {result ? (
          <div style={{ padding: '8px 0' }}>
            <p style={{ fontSize: 14, color: 'var(--ink-primary)', margin: '0 0 8px' }}>
              <strong>{result.inserted}</strong> imported · <strong>{result.updated}</strong> updated · <strong>{result.skipped}</strong> skipped · <strong>{result.rejected}</strong> rejected{result.failed ? ` · ${result.failed} failed` : ''}
            </p>
            {result.error && <p style={{ fontSize: 12, color: 'var(--danger)', margin: '0 0 8px' }}>First error: {result.error}</p>}
            <button onClick={onClose} style={{ marginTop: 8, padding: '10px 16px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, color: 'var(--ink-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={updateExisting} onChange={e => setUpdateExisting(e.target.checked)} />
              Update existing contacts instead of skipping ({dupCount} match by email)
            </label>
            <div style={{ flex: 1, overflow: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'var(--hover-bg)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '8px 14px', borderBottom: '1px solid var(--border-subtle)', position: 'sticky', top: 0, background: 'var(--surface)' }}>
                {['#', 'Name', 'Email', 'Company', 'Status'].map((h, i) => (
                  <div key={i} style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
                ))}
              </div>
              {rows.map(r => {
                const ok = r.kind === 'ok'
                const dup = ok && !!existingIdFor(r.row)
                const name = ok ? `${r.row.first_name} ${r.row.last_name || ''}`.trim() : (_t(r.raw.first_name) || '—')
                return (
                  <div key={r.index} style={{ display: 'grid', gridTemplateColumns: COLS, padding: '8px 14px', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center', fontSize: 12, color: 'var(--ink-secondary)' }}>
                    <span style={{ color: 'var(--ink-tertiary)' }}>{r.index + 1}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ok ? (r.row.email || '—') : (_t(r.raw.email) || '—')}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ok ? (r.row.company_name || '—') : '—'}</span>
                    {!ok ? (
                      <span style={{ color: 'var(--danger)', fontSize: 11, fontWeight: 600 }}>✗ {r.reason}</span>
                    ) : dup ? (
                      <span style={{ color: 'var(--warning)', fontSize: 11, fontWeight: 600 }}>↻ Duplicate</span>
                    ) : (
                      <span style={{ color: 'var(--positive)', fontSize: 11, fontWeight: 600 }}>✓ New</span>
                    )}
                  </div>
                )
              })}
              {rows.length === 0 && <p style={{ padding: 20, fontSize: 12, color: 'var(--ink-tertiary)', textAlign: 'center', margin: 0 }}>No rows parsed from this file.</p>}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCommit} disabled={committing || okRows.length === 0}
                style={{ flex: 1, padding: '10px', background: okRows.length === 0 ? 'var(--field-bg)' : 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: okRows.length === 0 ? 'not-allowed' : 'pointer', opacity: okRows.length === 0 ? 0.5 : 1 }}>
                {committing ? 'Importing…' : updateExisting ? `Import ${newCount} + update ${dupCount}` : `Import ${newCount} new${dupCount ? ` (skip ${dupCount})` : ''}`}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  )
}
const SORT_OPTIONS = [
  { value: 'created_at_desc', label: 'Newest first' },
  { value: 'created_at_asc', label: 'Oldest first' },
  { value: 'first_name_asc', label: 'Name A→Z' },
  { value: 'first_name_desc', label: 'Name Z→A' },
]

function UpgradeRequiredModal({
  reason,
  onClose,
}: {
  reason: 'at_limit' | 'no_access'
  onClose: () => void
}) {
  const headline = reason === 'no_access'
    ? 'Your workspace is locked'
    : 'Contact limit reached'

  const body = reason === 'no_access'
    ? "You don't have access to contacts on your current plan. Reactivate or upgrade your subscription to continue."
    : "You've reached the contact limit for your current plan. Upgrade to add more."

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 18, padding: 28, width: 420, maxWidth: '90vw' }}
      >
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          {reason === 'no_access'
            ? <Lock style={{ width: 20, height: 20, color: 'var(--accent-text)' }} />
            : <Sparkles style={{ width: 20, height: 20, color: 'var(--accent-text)' }} />}
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 8px' }}>{headline}</h2>
        <p style={{ fontSize: 13, color: 'var(--ink-secondary)', lineHeight: 1.5, margin: '0 0 22px' }}>{body}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn-ghost" style={{ fontSize: 13 }}>Not now</button>
          {SHOW_BILLING_TAB && (
            <Link to="/app/settings/billing" onClick={onClose} className="btn-primary" style={{ fontSize: 13, textDecoration: 'none' }}>
              Go to billing
            </Link>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}

export function NewContactModal({ onClose, onCreated, contactCount, initialCompany }: { onClose: () => void; onCreated: () => void; contactCount: number; initialCompany?: { id: string; name: string } }) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    company_id: initialCompany?.id || null as string | null,
    company_name: initialCompany?.name || '', status: 'active',
    address_line1: '', address_line2: '',
    address_city: '', address_state: '', address_zip: '', address_country: '',
  })
  const [saving, setSaving] = useState(false)
  const hasContactsFeature = useFeature('contacts')
  const { atLimit } = useLimitCheck('contacts', contactCount)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

 const handleSave = async () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return }
    if (!workspace?.id) { toast.error('No workspace found — try refreshing'); return }
    if (!hasContactsFeature || atLimit) {
      toast.error("You've hit your plan's contact limit. Upgrade to add more.")
      return
    }
    setSaving(true)
    // CompanyPicker is the canonical path for setting company_id. Top-level
    // creation flow now writes both company_id and the denormalized
    // company_name; CompanyDetail-launched creation pre-fills via initialCompany.
    // If the operator typed a name without picking, company_id stays null and
    // we persist the free-text company_name as a soft-link they can resolve
    // later from ContactDetail.
    const { error } = await supabase.from('contacts').insert({
      ...form,
      workspace_id: workspace.id,
    }).select()
    setSaving(false)
    if (error) {
      toast.error(`Error: ${error.message}`)
      console.error('Supabase error:', error)
      return 
    }
    toast.success('Contact created')
    onCreated()
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New contact</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>First name *</label>
              <input value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="Alex" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last name</label>
              <input value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Johnson" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="alex@company.com" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Phone</label>
            <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 (555) 000-0000" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Company</label>
            <CompanyPicker
              workspaceId={workspace?.id || ''}
              value={{ id: form.company_id, name: form.company_name }}
              onChange={next => setForm(p => ({ ...p, company_id: next.id, company_name: next.name }))}
              disabled={!!initialCompany}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="active">Active</option>
              <option value="lead">Lead</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Address (optional)</p>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Address line 1</label>
              <input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="123 Main St" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Address line 2</label>
              <input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="Suite 4B (optional)" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>City</label>
                <input value={form.address_city} onChange={e => set('address_city', e.target.value)} placeholder="New York" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>State</label>
                <input value={form.address_state} onChange={e => set('address_state', e.target.value)} placeholder="NY" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>ZIP</label>
                <input value={form.address_zip} onChange={e => set('address_zip', e.target.value)} placeholder="10001" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Country</label>
              <input value={form.address_country} onChange={e => set('address_country', e.target.value)} placeholder="US" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>{saving ? 'Saving...' : 'Create contact'}</button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function DeleteContactsModal({ count, onConfirm, onClose }: {
  count: number
  onConfirm: () => void
  onClose: () => void
}) {
  const [typed, setTyped] = useState('')
  const requiresTyping = count >= 2
  const canConfirm = !requiresTyping || typed === 'DELETE'

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 460, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24 }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--danger-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trash2 style={{ width: 18, height: 18, color: 'var(--danger)' }} />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>
            Delete {count} {count === 1 ? 'contact' : 'contacts'}?
          </h2>
        </div>

        <p style={{ fontSize: 13, color: 'var(--ink-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>
          This action cannot be undone. Deleting {count === 1 ? 'this contact' : 'these contacts'} will also permanently delete:
        </p>

        <ul style={{ fontSize: 13, color: 'var(--ink-secondary)', lineHeight: 1.7, margin: '0 0 20px', paddingLeft: 20 }}>
          <li>All associated deals</li>
          <li>All associated conversations and messages</li>
          <li>All associated invoices and quotes</li>
          <li>All associated projects and field jobs</li>
        </ul>

        {requiresTyping && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Type DELETE to confirm
            </label>
            <input
              value={typed}
              onChange={e => setTyped(e.target.value)}
              autoFocus
              style={{ width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '9px 16px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, color: 'var(--ink-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={!canConfirm}
            style={{ padding: '9px 16px', background: canConfirm ? 'var(--danger-dim)' : 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)', border: `1px solid ${canConfirm ? 'var(--danger-border)' : 'color-mix(in srgb, var(--danger) var(--status-tint-fill), transparent)'}`, borderRadius: 9, color: canConfirm ? 'var(--danger)' : 'color-mix(in srgb, var(--danger) var(--status-tint-edge), transparent)', fontSize: 13, fontWeight: 600, cursor: canConfirm ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Trash2 style={{ width: 13, height: 13 }} /> Delete {count === 1 ? 'contact' : `${count} contacts`}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default function Contacts() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [lifecycleFilter, setLifecycleFilter] = useState('all')
  const [tagFilter, setTagFilter] = useState('all')
  const [sort, setSort] = useState('created_at_desc')
  const [showNewModal, setShowNewModal] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState<'at_limit' | 'no_access' | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showSortMenu, setShowSortMenu] = useState(false)

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['contacts', 'all', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('contacts').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const hasContactsFeature = useFeature('contacts')
  const { atLimit, isLoading: limitsLoading } = useLimitCheck('contacts', contacts.length)

  const tryOpenNew = () => {
    // Gates intentionally fail closed: while limits are loading we deny.
    if (limitsLoading) return
    if (!hasContactsFeature) { setUpgradeReason('no_access'); return }
    if (atLimit) { setUpgradeReason('at_limit'); return }
    setShowNewModal(true)
  }

  // CSV export (import/export CP1). The canonical round-trip column set CP2's
  // import will consume — order + header names are the contract. company_name is
  // the DENORMALIZED row field (not the uuid), so the export round-trips: import
  // resolves the name back to company_id. tags (text[]) → semicolon-joined inside
  // a quoted cell. System/awkward cols (id, timestamps, portal_*, custom_fields)
  // are intentionally excluded.
  const exportCsv = () => {
    if (contacts.length === 0) return
    // RFC-4180: quote fields containing a comma, quote, or newline (mirror PortalSpend).
    const esc = (v: any) => {
      const s = v == null ? '' : String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const HEADERS = ['first_name', 'last_name', 'email', 'phone', 'company_name', 'status', 'tags', 'lifecycle_stage', 'notes', 'address_line1', 'address_line2', 'city', 'state', 'zip', 'country']
    const lines = (contacts as any[]).map(c => [
      esc(c.first_name), esc(c.last_name), esc(c.email), esc(c.phone),
      esc(c.company_name), esc(c.status),
      esc(Array.isArray(c.tags) ? c.tags.join('; ') : ''),
      esc(c.lifecycle_stage), esc(c.notes),
      esc(c.address_line1), esc(c.address_line2),
      esc(c.address_city), esc(c.address_state), esc(c.address_zip), esc(c.address_country),
    ].join(','))
    const csv = [HEADERS.join(','), ...lines].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // CSV import (CP2): file → Papa.parse → validateContactRow → preview modal.
  const csvInputRef = useRef<HTMLInputElement>(null)
  const [importRows, setImportRows] = useState<ImportPreviewRow[] | null>(null)
  const onCsvFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim().toLowerCase(),
      complete: (res) => {
        setImportRows((res.data || []).map((raw, i) => validateContactRow(raw, i)))
        if (csvInputRef.current) csvInputRef.current.value = ''
      },
      error: (err) => {
        toast.error(`CSV parse failed: ${err.message}`)
        if (csvInputRef.current) csvInputRef.current.value = ''
      },
    })
  }

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from('contacts').delete().in('id', ids)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['contacts'] }); setSelected(new Set()); toast.success('Deleted') },
    onError: () => toast.error('Failed to delete'),
  })

  const filtered = useMemo(() => {
    let list = [...contacts]
    if (statusFilter !== 'all') list = list.filter((c: any) => c.status === statusFilter)
    if (lifecycleFilter !== 'all') list = list.filter((c: any) => (c.lifecycle_stage || 'lead') === lifecycleFilter)
    if (tagFilter !== 'all') list = list.filter((c: any) => (c.tags || []).includes(tagFilter))
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((c: any) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.company_name?.toLowerCase().includes(q))
    }
    const sortMap: Record<string, [string, string]> = {
      'created_at_desc': ['created_at', 'desc'],
      'created_at_asc': ['created_at', 'asc'],
      'first_name_asc': ['first_name', 'asc'],
      'first_name_desc': ['first_name', 'desc'],
    }
    const [field, dir] = sortMap[sort] || ['created_at', 'desc']
    list.sort((a: any, b: any) => {
      const cmp = (a[field] || '') < (b[field] || '') ? -1 : (a[field] || '') > (b[field] || '') ? 1 : 0
      return dir === 'desc' ? -cmp : cmp
    })
    return list
  }, [contacts, search, statusFilter, lifecycleFilter, tagFilter, sort])

  // Distinct tags present across the loaded contacts → the tag-filter dropdown.
  const allTags = useMemo(() => Array.from(new Set((contacts as any[]).flatMap((c: any) => c.tags || []))).sort(), [contacts])

  const toggleSelect = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleAll = () => selected.size === filtered.length ? setSelected(new Set()) : setSelected(new Set(filtered.map((c: any) => c.id)))

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Contacts</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
              {contacts.length} total{filtered.length !== contacts.length ? ` · ${filtered.length} shown` : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {selected.size === 2 && (
              <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                onClick={() => setShowMergeModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 10, color: 'var(--accent-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <GitMerge style={{ width: 13, height: 13 }} /> Merge
              </motion.button>
            )}
            {selected.size > 0 && (
              <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                onClick={() => setShowDeleteModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', borderRadius: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <Trash2 style={{ width: 13, height: 13 }} /> Delete {selected.size}
              </motion.button>
            )}
            <input ref={csvInputRef} type="file" accept=".csv,text/csv" onChange={onCsvFile} style={{ display: 'none' }} />
            <button onClick={() => csvInputRef.current?.click()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 10, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <Upload style={{ width: 14, height: 14 }} /> Import CSV
            </button>
            {contacts.length > 0 && (
              <button onClick={exportCsv}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 10, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <Download style={{ width: 14, height: 14 }} /> Export CSV
              </button>
            )}
            <button onClick={tryOpenNew} disabled={limitsLoading} className="btn-primary">
              <Plus style={{ width: 15, height: 15 }} /> New contact
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts..."
              style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {['all', ...STATUSES].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: statusFilter === s ? 'var(--accent)' : 'var(--hover-bg)', color: statusFilter === s ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: statusFilter === s ? 'none' : '1px solid var(--border-subtle)' }}>
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
          {/* Lifecycle filter — composes (ANDs) with status + search. */}
          <select value={lifecycleFilter} onChange={e => setLifecycleFilter(e.target.value)}
            style={{ padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--ink-secondary)', outline: 'none', cursor: 'pointer', textTransform: 'capitalize' }}>
            <option value="all">Any lifecycle</option>
            {LIFECYCLES.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          {/* Tag filter — only shown when tags exist; composes (ANDs) with the others. */}
          {allTags.length > 0 && (
            <select value={tagFilter} onChange={e => setTagFilter(e.target.value)}
              style={{ padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--ink-secondary)', outline: 'none', cursor: 'pointer' }}>
              <option value="all">Any tag</option>
              {(allTags as string[]).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <div style={{ position: 'relative', marginLeft: 'auto' }}>
            <button onClick={() => setShowSortMenu(!showSortMenu)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--ink-secondary)', cursor: 'pointer' }}>
              <ArrowUpDown style={{ width: 12, height: 12 }} />
              {SORT_OPTIONS.find(o => o.value === sort)?.label}
            </button>
            <AnimatePresence>
              {showSortMenu && (
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                  style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, minWidth: 180 }}>
                  {SORT_OPTIONS.map(opt => (
                    <button key={opt.value} onClick={() => { setSort(opt.value); setShowSortMenu(false) }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: sort === opt.value ? 'var(--accent-text)' : 'var(--ink-secondary)', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      {opt.label}
                      {sort === opt.value && <Check style={{ width: 12, height: 12 }} />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden' }}>
          {/* Column header — desktop only. Cards on mobile are self-labeling
              (avatar+name as primary, icons in front of email/phone/company)
              so the header strip is redundant and just consumes vertical
              space. */}
          {!isMobile && (
          <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 200px 160px 100px 80px', padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} style={{ width: 14, height: 14, cursor: 'pointer', accentColor: 'var(--accent)' }} />
            </div>
            {['Name', 'Company', 'Contact', 'Status', ''].map((h, i) => (
              <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
            ))}
          </div>
          )}
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
              <Users style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>
                {search || statusFilter !== 'all' ? 'No contacts match your filters' : 'No contacts yet'}
              </p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>
                {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Add your first contact to get started'}
              </p>
              {!search && statusFilter === 'all' && (
                <button onClick={tryOpenNew} disabled={limitsLoading} className="btn-primary" style={{ margin: '0 auto' }}>
                  <Plus style={{ width: 14, height: 14 }} /> Add contact
                </button>
              )}
            </div>
          ) : (
            filtered.map((contact: any, i: number) => {
              const isSelected = selected.has(contact.id)
              const sc = getStatusColor(contact.status)
              const name = `${contact.first_name} ${contact.last_name || ''}`.trim()
              const isLast = i === filtered.length - 1

              // Mobile card layout. Same onClick → navigate as desktop;
              // hover background also preserved (no-op on touch but consistent).
              // Bulk-select checkbox is intentionally dropped on mobile —
              // tap-to-select on a tiny checkbox is awkward on phones and
              // bulk-delete is uncommon as a mobile workflow. If we want
              // mobile bulk-select later, long-press is the canonical pattern.
              if (isMobile) {
                return (
                  <motion.div key={contact.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                    onClick={() => navigate(`/app/contacts/${contact.id}`)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', background: isSelected ? 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)' : 'transparent', transition: 'background 150ms ease' }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--hover-bg)' }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}>
                    {/* Primary row: avatar + name (with optional company sub-line) + status badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Avatar name={name || 'U'} size={36} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || 'Unnamed contact'}</p>
                        {contact.company_name && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                            <Building2 style={{ width: 11, height: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.company_name}</span>
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, textTransform: 'capitalize', flexShrink: 0, alignSelf: 'flex-start' }}>
                        {contact.status}
                      </span>
                    </div>
                    {/* Contact methods + updated-at meta */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {contact.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Mail style={{ width: 12, height: 12, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: 'var(--ink-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.email}</span>
                        </div>
                      )}
                      {contact.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Phone style={{ width: 12, height: 12, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{contact.phone}</span>
                        </div>
                      )}
                      {!contact.email && !contact.phone && (
                        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>No contact info</span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 2 }}>
                        Updated {formatRelativeDate(contact.updated_at)}
                      </span>
                    </div>
                  </motion.div>
                )
              }

              // Desktop grid row — unchanged.
              return (
                <motion.div key={contact.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                  onClick={() => navigate(`/app/contacts/${contact.id}`)}
                  style={{ display: 'grid', gridTemplateColumns: '40px 1fr 200px 160px 100px 80px', padding: '12px 16px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer', background: isSelected ? 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)' : 'transparent', transition: 'background 150ms ease', alignItems: 'center' }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--hover-bg)' }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}>
                  <div onClick={e => { e.stopPropagation(); toggleSelect(contact.id) }}>
                    <input type="checkbox" checked={isSelected} onChange={() => {}} style={{ width: 14, height: 14, cursor: 'pointer', accentColor: 'var(--accent)' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <Avatar name={name || 'U'} size={32} />
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{name}</p>
                      {contact.tags && contact.tags.length > 0 && (
                        <div style={{ display: 'flex', gap: 4, marginTop: 2, alignItems: 'center' }}>
                          {contact.tags.slice(0, 2).map((t: string) => (
                            <span key={t} style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 100, background: 'var(--hover-bg)', color: 'var(--ink-tertiary)', border: '1px solid var(--border-subtle)' }}>{t}</span>
                          ))}
                          {contact.tags.length > 2 && <span style={{ fontSize: 9, color: 'var(--ink-tertiary)' }}>+{contact.tags.length - 2}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {contact.company_name
                      ? <><Building2 style={{ width: 12, height: 12, color: 'var(--ink-tertiary)', flexShrink: 0 }} /><span style={{ fontSize: 13, color: 'var(--ink-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.company_name}</span></>
                      : <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>—</span>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {contact.email && <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Mail style={{ width: 11, height: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }} /><span style={{ fontSize: 12, color: 'var(--ink-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.email}</span></div>}
                    {contact.phone && <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Phone style={{ width: 11, height: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }} /><span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{contact.phone}</span></div>}
                    {!contact.email && !contact.phone && <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>No contact info</span>}
                  </div>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, textTransform: 'capitalize' }}>
                      {contact.status}
                    </span>
                    {/* Lifecycle indicator (token-only color) */}
                    <span style={{ display: 'block', marginTop: 4, fontSize: 10, fontWeight: 600, color: LIFECYCLE_TEXT[contact.lifecycle_stage || 'lead'] || 'var(--ink-tertiary)', textTransform: 'capitalize' }}>
                      {contact.lifecycle_stage || 'lead'}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-tertiary)', textAlign: 'right' }}>
                    {formatRelativeDate(contact.updated_at)}
                  </div>
                </motion.div>
              )
            })
          )}
        </div>
      </div>
      <AnimatePresence>
        {importRows && workspace?.id && (
          <ContactsImportModal
            workspaceId={workspace.id}
            rows={importRows}
            onClose={() => setImportRows(null)}
            onImported={() => { /* list invalidated inside the modal; keep modal open for the summary */ }}
          />
        )}
        {showNewModal && (
          <NewContactModal
            onClose={() => setShowNewModal(false)}
            onCreated={() => queryClient.invalidateQueries({ queryKey: ['contacts'] })}
            contactCount={contacts.length}
          />
        )}
        {upgradeReason && (
          <UpgradeRequiredModal
            reason={upgradeReason}
            onClose={() => setUpgradeReason(null)}
          />
        )}
        {showDeleteModal && (
          <DeleteContactsModal
            count={selected.size}
            onConfirm={() => {
              deleteMutation.mutate(Array.from(selected))
              setShowDeleteModal(false)
            }}
            onClose={() => setShowDeleteModal(false)}
          />
        )}
        {showMergeModal && selected.size === 2 && (() => {
          const [aId, bId] = Array.from(selected)
          const recordA = contacts.find((c: any) => c.id === aId)
          const recordB = contacts.find((c: any) => c.id === bId)
          if (!recordA || !recordB) return null
          return (
            <MergeModal entityType="contact" recordA={recordA} recordB={recordB}
              onClose={() => setShowMergeModal(false)}
              onMerged={() => { queryClient.invalidateQueries({ queryKey: ['contacts'] }); setSelected(new Set()); setShowMergeModal(false) }} />
          )
        })()}
      </AnimatePresence>
    </div>
  )
}